/**
 * useStageEditor — orchestration seam for the pipeline Stage editor (SE-5488).
 *
 * Composes the raw phase mutations + the automation-rule enable primitive into
 * the five editor actions (rename, flag-edit, add, reorder, delete) and the
 * delete-guard flow, keeping all branching in `stageEditorOps` so it stays pure
 * and unit-testable. The hook itself only threads the board's live data through
 * those pure decisions and awaits the injected mutations, so a test can drive
 * every path with plain jest mocks — no Apollo required.
 *
 * Delete flow (AC2 + AC3):
 * - {@link UseStageEditorResult.previewDeletion} resolves the plan purely.
 * - {@link UseStageEditorResult.confirmDeletion} pauses every referencing rule
 *   via `SetPipelineAutomationRuleEnabled(id, false)` FIRST; if any pause fails
 *   it surfaces the failure and does NOT delete (partial-failure safety — a
 *   half-applied delete would orphan rules pointing at a gone stage). Only once
 *   all pauses succeed does it delete the phase.
 * @module features/player-kanban/hooks/useStageEditor
 */

import { useCallback, useMemo } from "react";

import { AnalyticsTracker } from "@/features/analytics";
import type { TPipelineStageEditAction } from "@/features/analytics";
import {
  type CreateKanbanPhaseInput,
  KanbanPipelineTemplate,
  type PipelineAutomationRuleFragmentFragment,
  type UpdateKanbanPhaseInput,
  type UpdateKanbanPhasePositionsInput,
} from "@/generated/graphql";

import { computePhasePositions } from "../utils/phasePositions";
import {
  buildStageRenameInput,
  type EditableStage,
  nextAppendedStagePosition,
  resolveStageDeletion,
  sortStagesByPosition,
  type StageCardRef,
  type StageDeletionPlan,
} from "../utils/stageEditorOps";

/** Injected write primitives + live board data for {@link useStageEditor}. */
export interface UseStageEditorDeps {
  /** The pipeline being edited. */
  readonly kanbanBoardId: string;
  /** Current stages (any order — the hook sorts by position). */
  readonly stages: readonly EditableStage[];
  /** Current cards (only `kanbanPhaseId` is read, for the occupancy guard). */
  readonly cards: readonly StageCardRef[];
  /**
   * Whether the card set is fully loaded (no further pages). When false, the
   * occupancy guard cannot prove a stage empty and deletion fails closed.
   */
  readonly cardsComplete: boolean;
  /** Current automation rules (for the rule-impact warning + pause). */
  readonly rules: readonly PipelineAutomationRuleFragmentFragment[];
  /**
   * Whether the rule set is fully loaded (no further pages). When false, the
   * rule-impact warning degrades to a generic caveat rather than a complete
   * named list.
   */
  readonly rulesComplete: boolean;
  /** Runs `UpdateKanbanPhase(id, input)`. */
  readonly updatePhase: (
    id: string,
    input: UpdateKanbanPhaseInput
  ) => Promise<unknown>;
  /** Runs `CreateKanbanPhase(input)`. */
  readonly createPhase: (input: CreateKanbanPhaseInput) => Promise<unknown>;
  /** Runs `DeleteKanbanPhase(id)`. */
  readonly deletePhase: (id: string) => Promise<unknown>;
  /** Runs `UpdateKanbanPhasePositions(input)`. */
  readonly updatePhasePositions: (
    input: UpdateKanbanPhasePositionsInput
  ) => Promise<unknown>;
  /** Runs `SetPipelineAutomationRuleEnabled(id, isEnabled)`. */
  readonly setRuleEnabled: (
    ruleId: string,
    isEnabled: boolean
  ) => Promise<unknown>;
  /**
   * The edited board's recruitment motion, for the SE-5508
   * `pipeline_stage_edited` telemetry only. `null`/undefined is a meaningful
   * value (a migrated SE-5505 board), not a missing one — it reports as
   * "custom" alongside an explicitly-CUSTOM board, because neither was seeded
   * from a template.
   */
  readonly pipelineTemplate?: KanbanPipelineTemplate | null;
}

/** Outcome of {@link UseStageEditorResult.confirmDeletion}. */
export type StageDeletionOutcome =
  | { readonly ok: true }
  | {
      /** A rule pause failed; the stage was NOT deleted. */
      readonly ok: false;
      /** The rule whose pause failed. */
      readonly failedRule: PipelineAutomationRuleFragmentFragment;
    };

/** Public API of {@link useStageEditor}. */
export interface UseStageEditorResult {
  /** Rename a stage — sends ONLY the name, preserving protection (AC1). */
  readonly renameStage: (stageId: string, name: string) => Promise<void>;
  /** Toggle a stage's protected flag (edited inside the editor, per scope). */
  readonly setStageProtected: (
    stageId: string,
    isProtected: boolean
  ) => Promise<void>;
  /** Toggle a stage's terminal flag (edited inside the editor, per scope). */
  readonly setStageTerminal: (
    stageId: string,
    isTerminal: boolean
  ) => Promise<void>;
  /**
   * Set (or clear) a stage's time-in-stage target in days (SE-5497). `null`
   * clears the target; a positive integer sets it. Additive to the existing
   * UpdateKanbanPhase mutation.
   */
  readonly setStageTimeTarget: (
    stageId: string,
    timeTargetDays: number | null
  ) => Promise<void>;
  /** Add a new stage at the end of the board. */
  readonly addStage: (name: string) => Promise<void>;
  /** Persist a new visual order for the whole board (AC4). */
  readonly reorderStages: (orderedStageIds: readonly string[]) => Promise<void>;
  /** Purely resolve what deleting a stage requires (AC2/AC3). */
  readonly previewDeletion: (stageId: string) => StageDeletionPlan | null;
  /**
   * Execute a confirmed delete: pause every referencing rule, then delete.
   * Aborts (without deleting) if any pause fails.
   * @param stageId - The stage to delete.
   * @returns The {@link StageDeletionOutcome}.
   */
  readonly confirmDeletion: (stageId: string) => Promise<StageDeletionOutcome>;
}

/**
 * Pause a list of rules one at a time, stopping at the first failure.
 *
 * Recursive rather than a `for`-await loop so the pauses are strictly
 * sequential AND fail-fast (a later pause never fires once an earlier one has
 * failed — pausing rules for a delete we are about to abort would be wrong).
 * @param rules - The rules still to pause.
 * @param setRuleEnabled - The enable/pause primitive.
 * @returns The first rule whose pause threw, or null when all succeeded.
 */
const pauseRulesSequentially = async (
  rules: readonly PipelineAutomationRuleFragmentFragment[],
  setRuleEnabled: (ruleId: string, isEnabled: boolean) => Promise<unknown>
): Promise<PipelineAutomationRuleFragmentFragment | null> => {
  const [head, ...tail] = rules;
  if (!head) return null;
  try {
    await setRuleEnabled(head.id, false);
  } catch {
    return head;
  }
  return pauseRulesSequentially(tail, setRuleEnabled);
};

/**
 * Wire the Stage-editor actions over injected mutations + live board data.
 * @param deps - {@link UseStageEditorDeps}.
 * @returns The {@link UseStageEditorResult}.
 */
export const useStageEditor = (
  deps: UseStageEditorDeps
): UseStageEditorResult => {
  const {
    kanbanBoardId,
    stages,
    cards,
    cardsComplete,
    rules,
    rulesComplete,
    updatePhase,
    createPhase,
    deletePhase,
    updatePhasePositions,
    setRuleEnabled,
    pipelineTemplate,
  } = deps;

  /**
   * SE-5508 `pipeline_stage_edited`.
   *
   * One emitter shared by all six editor actions, called only AFTER the
   * action's mutation has resolved — a rejected `updatePhase` / `createPhase`
   * / `deletePhase` propagates out of the action (the container's
   * `runRowWrite` turns it into an error toast) and never reaches this line,
   * so a failed edit emits nothing.
   *
   * `protected_flag_touched` is true for exactly the two gate flags
   * (`isProtected`, `isTerminal`) that `resolveStageMoveGate` reads to decide
   * whether a move into the stage is sensitive. A rename cannot set them —
   * `buildStageRenameInput` sends only `{ name }` — so a rename always
   * reports false.
   */
  const logStageEdit = useCallback(
    (action: TPipelineStageEditAction) => {
      void AnalyticsTracker.pipeline.logStageEdited({
        action,
        template_or_custom:
          pipelineTemplate && pipelineTemplate !== KanbanPipelineTemplate.Custom
            ? "template"
            : "custom",
        protected_flag_touched:
          action === "set_protected" || action === "set_terminal",
        board_id: kanbanBoardId,
      });
    },
    [kanbanBoardId, pipelineTemplate]
  );

  const renameStage = useCallback(
    async (stageId: string, name: string) => {
      await updatePhase(stageId, buildStageRenameInput(name));
      logStageEdit("rename");
    },
    [updatePhase, logStageEdit]
  );

  const setStageProtected = useCallback(
    async (stageId: string, isProtected: boolean) => {
      await updatePhase(stageId, { isProtected });
      logStageEdit("set_protected");
    },
    [updatePhase, logStageEdit]
  );

  const setStageTerminal = useCallback(
    async (stageId: string, isTerminal: boolean) => {
      await updatePhase(stageId, { isTerminal });
      logStageEdit("set_terminal");
    },
    [updatePhase, logStageEdit]
  );

  const setStageTimeTarget = useCallback(
    async (stageId: string, timeTargetDays: number | null) => {
      await updatePhase(stageId, { timeTargetDays });
      logStageEdit("set_time_target");
    },
    [updatePhase, logStageEdit]
  );

  const addStage = useCallback(
    async (name: string) => {
      await createPhase({
        kanbanBoardId,
        name,
        position: nextAppendedStagePosition(
          stages.map(stage => stage.position)
        ),
      });
      logStageEdit("add");
    },
    [createPhase, kanbanBoardId, stages, logStageEdit]
  );

  const reorderStages = useCallback(
    async (orderedStageIds: readonly string[]) => {
      // SE-6535: the payload must rebase clear of every position the board
      // currently holds. The backend applies these updates sequentially under a
      // non-deferred unique (boardId, position) constraint, so reusing live
      // positions — as the old `(index + 1) * GAP` scheme did — makes an
      // adjacent swap collide mid-transaction and roll the whole reorder back.
      await updatePhasePositions({
        kanbanBoardId,
        phasePositions: computePhasePositions(orderedStageIds, {
          currentPositions: stages.map(stage => stage.position),
        }),
      });
      logStageEdit("reorder");
    },
    [updatePhasePositions, kanbanBoardId, stages, logStageEdit]
  );

  const previewDeletion = useCallback(
    (stageId: string): StageDeletionPlan | null => {
      const stage = stages.find(candidate => candidate.id === stageId);
      if (!stage) return null;
      return resolveStageDeletion({
        stage,
        cards,
        rules,
        cardsComplete,
        rulesComplete,
      });
    },
    [stages, cards, rules, cardsComplete, rulesComplete]
  );

  const confirmDeletion = useCallback(
    async (stageId: string): Promise<StageDeletionOutcome> => {
      const plan = previewDeletion(stageId);
      // The stage vanished between preview and confirm (e.g. a refetch removed
      // it). Fail closed — never fall through to deletePhase on stale data,
      // which would bypass the AC2/AC3 guards entirely (SE-5488 review).
      if (!plan) {
        throw new Error("Stage is no longer available to delete.");
      }
      // A blocked plan must never reach confirm — the caller gates on it — but
      // guard here too so a stray confirm cannot bypass AC3 / the occupancy
      // fail-closed.
      if (plan.status === "blocked-has-cards") {
        throw new Error("Cannot delete a stage that still holds cards.");
      }
      if (plan.status === "blocked-unknown-occupancy") {
        throw new Error(
          "Cannot confirm the stage is empty yet. Please try again."
        );
      }

      if (plan.status === "warn-rules") {
        const failedRule = await pauseRulesSequentially(
          plan.rules,
          setRuleEnabled
        );
        if (failedRule) return { ok: false, failedRule };
      }

      // After `deletePhase` resolves, and after the `{ ok: false }` rule-pause
      // abort above — a delete that was abandoned because a rule pause failed
      // did not edit the board, so it emits nothing.
      await deletePhase(stageId);
      logStageEdit("delete");
      return { ok: true };
    },
    [previewDeletion, setRuleEnabled, deletePhase, logStageEdit]
  );

  return useMemo(
    () => ({
      renameStage,
      setStageProtected,
      setStageTerminal,
      setStageTimeTarget,
      addStage,
      reorderStages,
      previewDeletion,
      confirmDeletion,
    }),
    [
      renameStage,
      setStageProtected,
      setStageTerminal,
      setStageTimeTarget,
      addStage,
      reorderStages,
      previewDeletion,
      confirmDeletion,
    ]
  );
};

/**
 * Convenience: the board's stages in canonical (position) order.
 * @param stages - Stages in any order.
 * @returns The position-sorted stages.
 */
export const orderedStages = <T extends { readonly position: number }>(
  stages: readonly T[]
): T[] => sortStagesByPosition(stages);
